import clamp from 'clamp';
import { Animated } from 'react-native';

import type { TableGridView } from '@gitbook/doc-core';

export const DEFAULT_TABLE_COLUMN_WIDTH = 100;
export const MIN_COLUMN_WIDTH = 40;
export const MAX_COLUMN_WIDTH = 800;

export type ColumnWidthsMap = Map<
    string,
    {
        controller: Animated.Value;
        animated: Animated.Value;
        width: number;
    }
>;

/**  Compute the size of each column. */
export function computeColumnWidths(
    view: TableGridView,
    editorWidth: number
): { widths: ColumnWidthsMap; totalWidth: number } {
    if (!view.useNewSizing) {
        return computeScaledColumnWidths(view, editorWidth);
    }

    const widths = new Map();

    // Calculate how much of the table is reserved by fixed-sized columns
    let reservedWidth = 0;

    view.columns.forEach((key) => {
        reservedWidth += view.columnWidths?.[key] || 0;
    });

    // Find out how much width is left after the fixed-size columns have been allocated.
    const remainingWidth = Math.max(editorWidth - reservedWidth, 0);

    // How many columns are auto-sized?
    const autosizeColumnCount = view.columns.length - Object.keys(view.columnWidths || {}).length;

    // How many px do we allocate per autosize column (rounded down)
    const widthPerColumn =
        autosizeColumnCount === 0 ? 0 : Math.floor(remainingWidth / autosizeColumnCount);

    // In order to avoid anti-aliasing, round the width per column to an integer and throw the remainder on the end
    const endOffset =
        autosizeColumnCount === 0 ? 0 : remainingWidth - widthPerColumn * autosizeColumnCount;

    let totalWidth = 0;

    // Calculate column widths as animated values
    view.columns.forEach((key) => {
        const isAutosized = typeof view.columnWidths?.[key] === 'undefined';
        const fixedWidth = view.columnWidths?.[key] || 0;

        let scaledWidth = isAutosized ? widthPerColumn : fixedWidth;

        // add the anti-aliased offset to the last column
        if (key === view.columns[view.columns.length - 1]) {
            scaledWidth += endOffset;
        }

        scaledWidth = clamp(
            scaledWidth,
            isAutosized ? DEFAULT_TABLE_COLUMN_WIDTH : MIN_COLUMN_WIDTH,
            MAX_COLUMN_WIDTH
        );
        totalWidth += scaledWidth;

        const controller = new Animated.Value(0);
        controller.setOffset(scaledWidth);
        widths.set(key, {
            controller,
            animated: Animated.diffClamp(
                controller,
                isAutosized ? DEFAULT_TABLE_COLUMN_WIDTH : MIN_COLUMN_WIDTH,
                MAX_COLUMN_WIDTH
            ),
            width: scaledWidth,
        });
    });

    return { widths, totalWidth };
}

/**  Compute the size of each column. */
function computeScaledColumnWidths(
    view: TableGridView,
    editorWidth: number
): { widths: ColumnWidthsMap; totalWidth: number } {
    // when we used scaled columns, the min width used to be 150, and the default 200
    const MIN_WIDTH = 150;
    const DEFAULT_WIDTH = 200;

    const widths = new Map();
    let totalWidth = 0;
    view.columns.forEach((key) => {
        const offset = view.columnWidths?.[key] || DEFAULT_WIDTH;
        totalWidth += offset;
        widths.set(key, offset);
    });

    // Scale up if it doesn't take the full-width
    let scale = 1;
    if (totalWidth < editorWidth) {
        scale = editorWidth / totalWidth;
        totalWidth = editorWidth;
    }
    // Convert value into animated values
    widths.forEach((offset, key) => {
        const controller = new Animated.Value(0);
        controller.setOffset(offset * scale);
        widths.set(key, {
            controller,
            animated: Animated.diffClamp(controller, MIN_WIDTH, MAX_COLUMN_WIDTH),
            width: offset * scale,
        });
    });

    return { widths, totalWidth };
}
